import Eyebrow from "./Eyebrow";
import React from "react";
import Reveal from "./Reveal";

export interface PageHeaderProps {
  title: React.ReactNode;
  /** Small kicker/eyebrow above the title. */
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Right-aligned actions (buttons, filters). */
  actions?: React.ReactNode;
  /** Accent hue of the glow wash behind the header. */
  accent?: "blue" | "amber";
  icon?: React.ReactNode;
  className?: string;
}

/**
 * The "Night Marquee" header — the site's signature element. A mono
 * station-sign eyebrow, a lake-to-amber gradient display title, and a lit
 * underglow bar beneath it, all on a top-lit banner card. One header system
 * for every list page and section so headers stop competing with each other.
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  eyebrow,
  subtitle,
  actions,
  accent = "blue",
  icon,
  className = "",
}) => {
  return (
    <Reveal
      className={`relative overflow-hidden rounded-2xl border border-gray-700/80 bg-gray-800/90 shadow-2xl ${className}`}
    >
      {/* top-lit sheen + accent glow wash */}
      <div className="pointer-events-none absolute inset-0 bg-panel-sheen" />
      <div
        className={`pointer-events-none absolute inset-0 opacity-70 ${
          accent === "amber"
            ? "bg-gradient-to-r from-accent-900/40 via-gray-800/10 to-transparent"
            : "bg-gradient-to-r from-blue-900/50 via-gray-800/10 to-transparent"
        }`}
      />
      <div className="relative flex flex-col gap-4 px-6 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <div className="flex items-center gap-4">
          {icon && (
            <div className="hidden rounded-2xl border border-gray-700 bg-gray-900/60 p-3 text-blue-300 sm:block">
              {icon}
            </div>
          )}
          <div>
            {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
            <h1 className="bg-marquee bg-clip-text font-display text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl">
              {title}
            </h1>
            {/* marquee underglow bar */}
            <div aria-hidden className="relative mt-3 h-1 w-24">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-400 via-accent-300 to-blue-400" />
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-400 via-accent-300 to-blue-400 blur-[6px]" />
            </div>
            {subtitle && (
              <p className="mt-3 max-w-2xl text-gray-400">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex shrink-0 items-center gap-3">{actions}</div>
        )}
      </div>
    </Reveal>
  );
};

export default PageHeader;
